import React, { useContext, useMemo, useState } from "react";
import { ApiContext } from "../../../context/MyContext";
import { EmptyState } from "../../../common/comms/EmptyState";
import { RetryBanner } from "../../../common/comms/RetryBanner";
import { SkeletonRows } from "../../../common/comms/SkeletonRows";
import { Toaster } from "../../../common/Toaster/Toaster";
import { LEAVE_STATUS, leaveStatusPill, leaveStatusToken } from "../core/leaveLookups";
import { formatLeaveRange, leaveDays } from "../core/leaveDates";
import LeaveDecisionModal from "./LeaveDecisionModal";

const FILTERS = [
  { id: LEAVE_STATUS.PENDING, label: "Pending" },
  { id: LEAVE_STATUS.APPROVED, label: "Approved" },
  { id: LEAVE_STATUS.REJECTED, label: "Rejected" },
  { id: "all", label: "All" },
];

const GRID =
  "grid grid-cols-[minmax(0,1.4fr)_100px_130px_44px_minmax(0,1.3fr)_110px_200px] gap-3 px-4";

const RINGS = ["#E8590C", "#12A594", "#3B82F6", "#E64980", "#2F9E44", "#F59F00", "#5C6B7A", "#9C6644"];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Initials and a stable ring colour per coach, as the design's roster does. */
function avatarOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  const initials = ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
  let hash = 0;
  for (const ch of String(name || "")) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return { initials, ring: RINGS[hash % RINGS.length] };
}

/** "today, 9:12 AM" / "yesterday" / "12 Oct". */
export function appliedLabel(iso, now = new Date()) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dayStart = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((dayStart(now) - dayStart(d)) / 86400000);
  if (diff === 0) {
    return `today, ${d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}`;
  }
  if (diff === 1) return "yesterday";
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** The manager tab's pill palette differs from the profile's for Pending/Approved. */
function managerPill(status) {
  const token = leaveStatusToken(status);
  if (token === LEAVE_STATUS.PENDING) return { label: "Pending", className: "text-[#C2410C] bg-[#FFF0E6]" };
  if (token === LEAVE_STATUS.APPROVED) return { label: "Approved", className: "text-[#1E7A45] bg-[#E7F5EC]" };
  if (token === LEAVE_STATUS.REJECTED) return { label: "Rejected", className: "text-[#B42318] bg-[#FDECEC]" };
  return leaveStatusPill(status);
}

/**
 * Support → Coaches → Leaves (Claude Design "Manager Leaves" 5a).
 *
 * Presentational over `useTeamLeaves`, which the page owns so the tab badge
 * and this list share one fetch.
 *
 * @param {Object} props
 * @param {ReturnType<import("../workflow/useTeamLeaves").useTeamLeaves>} props.team
 */
const ManagerLeavesTab = ({ team }) => {
  const { employeeData } = useContext(ApiContext) || {};
  const [filter, setFilter] = useState(LEAVE_STATUS.PENDING);
  const [decision, setDecision] = useState(null); // { leave, kind }
  const [toast, setToast] = useState(null);

  const approverName = [employeeData?.data?.first_name, employeeData?.data?.last_name]
    .filter(Boolean)
    .join(" ");

  const counts = useMemo(() => {
    const c = { all: team.rows.length };
    team.rows.forEach((r) => {
      const t = leaveStatusToken(r.status);
      c[t] = (c[t] || 0) + 1;
    });
    return c;
  }, [team.rows]);

  const list =
    filter === "all" ? team.rows : team.rows.filter((r) => leaveStatusToken(r.status) === filter);

  const handleConfirm = async (note) => {
    const { leave, kind } = decision;
    const first = String(leave.employee_name || "").trim().split(/\s+/)[0] || "The coach";
    const { cancelled } = await team.decide(leave, kind, note);
    setDecision(null);
    const message =
      kind === LEAVE_STATUS.REJECTED
        ? `Leave rejected · ${first} notified`
        : cancelled
          ? `Leave approved · ${cancelled} ${cancelled === 1 ? "booking" : "bookings"} cancelled · ${cancelled} ${
              cancelled === 1 ? "client" : "clients"
            } messaged`
          : `Leave approved · ${first} notified`;
    setToast({ message, type: "success", id: Date.now() });
  };

  const bookingsCell = (row) => {
    if (leaveStatusToken(row.status) !== LEAVE_STATUS.PENDING) {
      return row.cancelledCount != null
        ? { text: `${row.cancelledCount} cancelled`, className: "text-[#8A8178]" }
        : { text: "—", className: "text-[#8A8178]" };
    }
    if (row.bookings == null) {
      return team.bookingsError
        ? { text: "Unknown", className: "text-[#8A8178]" }
        : { text: "Checking…", className: "text-[#8A8178]" };
    }
    const n = row.bookings.length;
    return n
      ? { text: `${n} ${n === 1 ? "booking" : "bookings"}`, className: "text-[#C2410C]" }
      : { text: "None", className: "text-[#8A8178]" };
  };

  return (
    <div className="flex flex-col gap-[18px] text-[#1F2328]">
      {toast && <Toaster key={toast.id} message={toast.message} type={toast.type} />}

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const on = filter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              aria-pressed={on}
              onClick={() => setFilter(f.id)}
              className={`h-[34px] px-3.5 rounded-full text-[13px] font-bold border ${
                on ? "bg-[#F2601F] text-white border-[#F2601F]" : "bg-white text-[#1F2328] border-[#E8E5E1] hover:border-[#F2601F] hover:text-[#E8590C]"
              }`}
            >
              {f.label} · {counts[f.id] || 0}
            </button>
          );
        })}
      </div>

      {team.error ? (
        <RetryBanner message={team.error} onRetry={team.reload} />
      ) : team.loading && team.rows.length === 0 ? (
        <SkeletonRows variant="rows" count={4} />
      ) : (
        <div className="border border-[#EFEBE7] rounded-xl overflow-x-auto bg-white">
          <div className="min-w-[960px]">
            <div className={`${GRID} py-3 text-xs font-bold text-[#8A8178] border-b border-[#EFEBE7]`}>
              <span>COACH</span>
              <span>Type</span>
              <span>Dates</span>
              <span>Days</span>
              <span>Remarks</span>
              <span>Bookings</span>
              <span />
            </div>

            {list.length === 0 ? (
              <EmptyState title="No leave requests here." />
            ) : (
              list.map((row) => {
                const { initials, ring } = avatarOf(row.employee_name);
                const pending = leaveStatusToken(row.status) === LEAVE_STATUS.PENDING;
                const bk = bookingsCell(row);
                const pill = managerPill(row.status);
                const awaitingBookings = pending && row.bookings == null && !team.bookingsError;
                return (
                  <div
                    key={row.id}
                    className={`${GRID} py-3.5 items-center text-sm border-b border-[#F4F1EE] last:border-b-0`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className="w-[34px] h-[34px] rounded-full border-2 flex items-center justify-center text-xs font-extrabold flex-none"
                        style={{ borderColor: ring, color: ring }}
                      >
                        {initials}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="font-bold truncate">{row.employee_name || "—"}</span>
                        <span className="text-xs text-[#8A8178]">Applied {appliedLabel(row.created_at)}</span>
                      </div>
                    </div>
                    <span className="font-semibold truncate">{row.leave_type || "—"}</span>
                    <span>{formatLeaveRange(row, "–")}</span>
                    <span>{leaveDays(row)}</span>
                    <span className="text-[#5C554F] break-words">{row.reason || "—"}</span>
                    <span className={`text-[13px] font-bold ${bk.className}`}>{bk.text}</span>
                    <div className="flex justify-end gap-2">
                      {pending ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setDecision({ leave: row, kind: LEAVE_STATUS.REJECTED })}
                            className="h-[34px] px-3.5 rounded-lg border border-[#E8E5E1] bg-white text-[13px] font-bold hover:bg-[#F4F1EE]"
                          >
                            Reject
                          </button>
                          <button
                            type="button"
                            onClick={() => setDecision({ leave: row, kind: LEAVE_STATUS.APPROVED })}
                            disabled={awaitingBookings}
                            title={awaitingBookings ? "Checking this coach's bookings…" : undefined}
                            className="h-[34px] px-3.5 rounded-lg bg-[#F2601F] text-white text-[13px] font-bold hover:bg-[#E8590C] disabled:opacity-60"
                          >
                            Approve
                          </button>
                        </>
                      ) : (
                        <div className="flex flex-col items-end gap-[3px] min-w-0">
                          <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${pill.className}`}>
                            {pill.label}
                          </span>
                          {row.status_changed_at && (
                            <span className="text-xs text-[#8A8178] text-right">
                              {appliedLabel(row.status_changed_at)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <LeaveDecisionModal
        leave={decision?.leave || null}
        decision={decision?.kind}
        approverName={approverName}
        onClose={() => setDecision(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default ManagerLeavesTab;
